import { AfterViewInit, Directive, ElementRef, HostListener, Input, OnDestroy, Optional, Renderer2, inject } from '@angular/core';
import { NgControl, NgModel } from '@angular/forms';
import { App } from '@df/core/app';
import { WINDOW } from '@df/dom/globals';
import { Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';

export interface AutoSizeInputOptions {
    extraWidth: number;
    maxWidth: number;
    minWidth: number;
    includeBorders: boolean;
    includePadding: boolean;
    includePlaceholder: boolean;
    usePlaceholderWhenEmpty?: boolean;
}

export type WidthProperty = 'border-left-width' | 'border-right-width' | 'padding-left' | 'padding-right';

export const DEFAULT_AUTO_SIZE_INPUT_OPTIONS: AutoSizeInputOptions = {
    extraWidth: 0,
    maxWidth: -1,
    minWidth: -1,
    includeBorders: true,
    includePadding: true,
    includePlaceholder: true,
    usePlaceholderWhenEmpty: false
};

/**
 * An Angular directive that automatically adjusts the width of an input element to its content.
 *
 * @example
 * ```html
 * <input autoSizeInput>
 * ```
 */
@Directive({
    standalone: true,
    selector: '[autoSizeInput]'
})
export class AutoSizeInputDirective implements AfterViewInit, OnDestroy {
    /** Add extra width, in units of pixels. */
    @Input()
    extraWidth = this.defaultOptions.extraWidth;

    /** Sets maximum width, in units of pixels. */
    @Input()
    maxWidth = this.defaultOptions.maxWidth;

    /** Sets minimum width, in units of pixels. */
    @Input()
    minWidth = this.defaultOptions.minWidth;

    /** Includes border width, so that text is not cut off. Note only even left and right borders are supported at this time. */
    @Input()
    includeBorders = this.defaultOptions.includeBorders;

    /** Includes padding width, so that text is not cut off. */
    @Input()
    includePadding = this.defaultOptions.includePadding;

    /** Set placeholder text width as minimum width; Note that [minidth] will override this property. */
    @Input()
    includePlaceholder = this.defaultOptions.includePlaceholder;

    /** Sets width to placeholder width, only when value is empty. */
    @Input()
    usePlaceholderWhenEmpty = this.defaultOptions.usePlaceholderWhenEmpty;

    /**
     * If the value of the form control is an object but the input value is formatted setting this value to `true`
     * will use the value property of the input instead of retreiving the value from the form control or model.
     * This option is not globally configurable and must be set on each input.
     */
    @Input()
    useValueProperty = false;

    private subscriptions = new Subscription();

    private window = inject(WINDOW);

    constructor(
        private app: App,
        private element: ElementRef,
        private renderer: Renderer2,
        @Optional() private ngModel: NgModel,
        @Optional() private ngControl: NgControl
    ) { }

    get borderWidth(): number {
        return this.includeBorders ? this._sumPropertyValues(['border-right-width', 'border-left-width']) : 0;
    }

    get defaultOptions() {
        return DEFAULT_AUTO_SIZE_INPUT_OPTIONS;
    }

    get paddingWidth(): number {
        return this.includePadding ? this._sumPropertyValues(['padding-left', 'padding-right']) : 0;
    }

    get style() {
        return (this.window as Window).getComputedStyle(this.element.nativeElement, '');
    }

    ngAfterViewInit() {
        if (this.ngModel) {
            this.subscriptions.add(this.ngModel.valueChanges!.pipe(tap(() => this.updateWidth())).subscribe());
        } else if (this.ngControl) {
            this.subscriptions.add(this.ngControl.valueChanges!.pipe(tap(() => this.updateWidth())).subscribe());
            this.updateWidth();
        } else {
            this.updateWidth();
        }
    }

    @HostListener('input')
    onInput(): void {
        if (!this.ngModel && !this.ngControl) {
            this.updateWidth();
        }
    }
    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    private getInputValue(): string {
        let value: string | undefined;

        if (this.useValueProperty) {
            value = this._getProperty('value');
        } else if (this.ngModel) {
            value = this.ngModel.value;
        } else if (this.ngControl) {
            value = this.ngControl.value;
        }

        return value || this._getProperty('value') || '';
    }

    setWidth(width: number): void {
        const { nativeElement } = this.element;

        this.renderer.setStyle(nativeElement, 'width', Math.ceil(width) + 'px');
    }

    setWidthUsingText(text: string): void {
        this.setWidth(this.textWidth(text) + this.extraWidth + this.borderWidth + this.paddingWidth);
    }

    // Check placeholder width settings and set text width
    textForWidth(inputText: string, placeHolderText: string, setPlaceHolderWidth: boolean) {
        return setPlaceHolderWidth && (inputText.length === 0 || !this.usePlaceholderWhenEmpty) ? placeHolderText : inputText;
    }

    textWidth(value: string): number {
        if (this.app.isPlatformBrowser) {
            const ctx = this.renderer.createElement('canvas').getContext('2d');
            const { fontStyle, fontVariant, fontWeight, fontSize, fontFamily } = this.style;

            // font string format: {normal, normal, 700, 20px, Roboto, "Helvetica Neue", sans-serif}
            ctx.font = fontStyle + ' ' + fontVariant + ' ' + fontWeight + ' ' + fontSize + ' ' + fontFamily;

            return ctx.measureText(value).width;
        }

        return (value?.length || 1) * 5;
    }

    updateWidth(): void {
        const inputText = this.getInputValue();
        const placeHolderText = this._getProperty('placeholder');
        const inputTextWidth = this.textWidth(inputText) + this.extraWidth + this.borderWidth + this.paddingWidth;
        const setMinWidth = this.minWidth > 0 && this.minWidth > inputTextWidth;
        const setPlaceHolderWidth =
            this.includePlaceholder && placeHolderText.length > 0 && this.textWidth(placeHolderText) > this.textWidth(inputText);
        const setMaxWidth = this.maxWidth > 0 && this.maxWidth < inputTextWidth;
        if (setMinWidth) {
            this.setWidth(this.minWidth);
        } else if (setMaxWidth) {
            this.setWidth(this.maxWidth);
        } else {
            this.setWidthUsingText(this.textForWidth(inputText, placeHolderText, setPlaceHolderWidth));
        }
    }

    private _getProperty(property: 'value' | 'placeholder') {
        return this.element.nativeElement?.[property];
    }

    private _sumPropertyValues(properties: WidthProperty[]): number {
        if (!this.app.isPlatformBrowser) {
            return 0;
        }
        return properties.map(property => parseInt(this.style.getPropertyValue(property), 10)).reduce((a, b) => a + b, 0);
    }
}
